import type { JSX } from 'react';

import { Translate, TranslatePlural, useI18n } from '@change-corgi/core/react/i18n';
import { VisuallyHidden } from '@change-corgi/design-system/a11y';
import { ProgressIndicator } from '@change-corgi/design-system/components/navigation';
import { Box } from '@change-corgi/design-system/layout';
import { Text } from '@change-corgi/design-system/typography';

import { useContributionCount } from './hooks/useContributionCount';

export function ContributionCount(): JSX.Element {
	const { translate } = useI18n();
	const { completionPercentage, count, localizedCount, localizedGoal } = useContributionCount();

	return (
		<>
			<Box data-qa="contributions-thermometer">
				<ProgressIndicator
					variant="purple"
					aria-label={translate('corgi.psf.promote_or_share.contributions.thermometer.label')}
					completionPercentage={completionPercentage}
				/>
			</Box>
			<Box>
				<VisuallyHidden>
					<Text>
						<Translate
							value="corgi.psf.promote_or_share.contributions.count_bar.a11y.count"
							replacements={{ contributionCount: localizedCount }}
						/>
					</Text>
					<Text>
						<Translate
							value="corgi.psf.promote_or_share.contributions.count_bar.a11y.goal"
							replacements={{ nextGoal: localizedGoal }}
						/>
					</Text>
				</VisuallyHidden>
				<Box mb={8} sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }} aria-hidden>
					<Box>
						<Text fontWeight="bold" size="huge" color="secondary-purple">
							{localizedCount}
						</Text>
					</Box>
					<Box>
						<Text
							fontWeight="bold"
							size="huge"
							color="neutral-grey600"
							sx={{
								textAlign: 'right',
								display: 'inline-block',
								width: '100%',
							}}
						>
							{localizedGoal}
						</Text>
					</Box>
					<Box>
						<Text color="secondary-purple">
							<TranslatePlural value="corgi.psf.promote_or_share.contributions.count_bar.count" count={count} />
						</Text>
					</Box>
					<Box>
						<Text
							color="neutral-grey600"
							sx={{
								textAlign: 'right',
								display: 'inline-block',
								width: '100%',
							}}
						>
							<Translate value="corgi.psf.promote_or_share.contributions.count_bar.goal" />
						</Text>
					</Box>
				</Box>
			</Box>
		</>
	);
}
